// Section 19: Fire. 5% per season, Summer and Winter bias. One building burns (a stone house is
// 0.3x as likely, Section 9): Section 9's fire rule leaves a wood building at 20% condition and a
// stone one at 60%; food stored in it is 30% lost (a granary, or a dwelling when there is no
// granary, sharing the loss); one person is injured. A manned palisade halves the chance.
// Constants: config.events.fire, config.buildings, config.building.fire*.
import type { Config } from '../../config';
import { eventEntry, eventRng } from '../../core/content-helpers';
import { fireWeight, palisadeStrength } from '../../core/queries';
import { defineEvent, noModifiers } from '../../core/registry';
import type { Building, Delta, Person, ReadContext } from '../../core/types';

interface FirePlan {
  building: Building;
  food: number;
  injured: Person | null;
}

function plan(ctx: ReadContext, storedFoodLoss: number): FirePlan | null {
  const rng = eventRng(ctx, 'fire', 'plan');
  const buildings = Object.keys(ctx.state.buildings)
    .sort()
    .map((id) => ctx.state.buildings[id as keyof typeof ctx.state.buildings])
    .filter((b): b is Building => b !== undefined && b.condition > 0);
  const weighted = buildings.map((b) => ({ item: b, weight: fireWeight(ctx.cfg, b) }));
  if (weighted.every((w) => w.weight <= 0)) return null;
  const building = rng.weighted(weighted);
  const def = ctx.registry.buildings[building.type];
  const food = ctx.state.stocks.food.amount;
  const granary = buildings.some((b) => b.type === 'granary');
  const dwellings = buildings.filter((b) => ctx.registry.buildings[b.type].beds > 0).length;
  let loss = 0;
  if (building.type === 'granary') loss = food * storedFoodLoss;
  else if (!granary && def.beds > 0 && dwellings > 0) loss = (food * storedFoodLoss) / dwellings;
  const people = Object.keys(ctx.state.people)
    .sort()
    .map((id) => ctx.state.people[id as keyof typeof ctx.state.people])
    .filter((p): p is Person => p !== undefined && p.status.injured === null);
  const residents = people.filter(
    (p) => ctx.state.households[p.householdId]?.buildingId === building.id,
  );
  const pool = residents.length > 0 ? residents : people;
  const injured = pool.length > 0 ? rng.pick(pool) : null;
  return { building, food: Math.round(loss * 10) / 10, injured };
}

export function fire(config: Config) {
  const c = config.events.fire;
  return defineEvent({
    id: 'fire',
    label: 'Fire',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['summer', 'winter'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) =>
      Object.keys(ctx.state.buildings).length === 0
        ? 0
        : c.baseChance * (1 - (1 - c.palisadeMul) * palisadeStrength(ctx.cfg, ctx.state)),
    text: (ctx) => {
      const p = plan(ctx, c.storedFoodLoss);
      return eventEntry('fire', {
        building: p?.building.type ?? '',
        food: p?.food ?? 0,
        name: p?.injured?.name ?? '',
      });
    },
    apply: (ctx) => {
      const p = plan(ctx, c.storedFoodLoss);
      if (p === null) return [];
      const out: Delta[] = [
        {
          kind: 'damageBuilding',
          buildingId: p.building.id,
          cause: 'fire',
          amount: -c.condition,
          source: 'event:fire',
        },
      ];
      if (p.food > 0) out.push({ kind: 'stock', resource: 'food', amount: -p.food, flow: 'other' });
      for (let i = 0; i < c.injuries && p.injured !== null; i++)
        out.push({ kind: 'injure', personId: p.injured.id, source: 'event:fire' });
      return out;
    },
    choices: [],
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
